import type { AxisProfile, CeilingProfile } from '@roomtreatment/core';
import type { PolyPt } from './polygon';
import { axisProfileFlat } from './ceiling';

const EPS = 1e-6;

/** Floor-plan bounding box in the room's x/z footprint (PolyPt uses x/y = world x/z). */
export interface RoomBBox {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/** Whether a ceiling profile actually deviates from flat. */
export function ceilingHasSlope(profile: CeilingProfile | null | undefined): boolean {
  return !!profile && (!axisProfileFlat(profile.x) || !axisProfileFlat(profile.y));
}

function axisHeight(ap: AxisProfile, t: number): number {
  const ns = [...ap.nodes].sort((a, b) => a[0] - b[0]);
  const tc = Math.max(0, Math.min(1, t));
  for (let i = 0; i < ns.length - 1; i++) {
    const [t0, z0] = ns[i], [t1, z1] = ns[i + 1];
    if (tc >= t0 - 1e-9 && tc <= t1 + 1e-9) {
      if (t1 - t0 < 1e-12) return z0;
      let u = (tc - t0) / (t1 - t0);
      if (ap.mode === 'smooth') u = u * u * (3 - 2 * u);
      return z0 + u * (z1 - z0);
    }
  }
  return ns[ns.length - 1][1];
}

/** Resolved ceiling height for normalised (tx, ty) in [0,1]. */
export function ceilingHeightAt(profile: CeilingProfile, tx: number, ty: number): number {
  const xActive = !axisProfileFlat(profile.x);
  const yActive = !axisProfileFlat(profile.y);
  const primary: 'x' | 'y' =
    xActive && yActive ? (profile.primary_axis === 'y' ? 'y' : 'x')
      : xActive ? 'x' : 'y';

  const primAp = primary === 'x' ? profile.x : profile.y;
  if (!primAp) return 0;
  const zp = axisHeight(primAp, primary === 'x' ? tx : ty);

  const secAp = primary === 'x' ? profile.y : profile.x;
  if (axisProfileFlat(secAp)) return zp;
  const zs = axisHeight(secAp!, primary === 'x' ? ty : tx);
  const zsMax = Math.max(...secAp!.nodes.map((n) => n[1]));
  return zp + (zs - zsMax);
}

/** True if either axis interpolates with smoothstep. */
export function ceilingIsSmooth(profile: CeilingProfile): boolean {
  return (!!profile.x && profile.x.mode === 'smooth')
    || (!!profile.y && profile.y.mode === 'smooth');
}

/** u-parameters along a floor segment where the ceiling profile has a kink. */
export function profileBreakUs(
  a: PolyPt, b: PolyPt, profile: CeilingProfile, bbox: RoomBBox,
): number[] {
  const us = new Set<number>([0, 1]);
  const cross = (iso: number, a0: number, b0: number) => {
    const d = b0 - a0;
    if (Math.abs(d) < EPS) return;
    const u = (iso - a0) / d;
    if (u > 1e-6 && u < 1 - 1e-6) us.add(u);
  };
  const wx = bbox.maxX - bbox.minX, wy = bbox.maxY - bbox.minY;
  if (!axisProfileFlat(profile.x)) {
    for (const [t] of profile.x!.nodes) cross(bbox.minX + t * wx, a.x, b.x);
  }
  if (!axisProfileFlat(profile.y)) {
    for (const [t] of profile.y!.nodes) cross(bbox.minY + t * wy, a.y, b.y);
  }
  return [...us].sort((p, q) => p - q);
}
